/**
 * Stream-overlay components rendered over the host's live preview AND
 * optionally broadcast to viewers via the `overlays` WS frame.
 *
 * Split into tiny pieces so AdvancedDirectorPanel can compose them with the
 * existing newsroom overlays (lower-third, ticker, breaking banner).
 */
import React, { useEffect, useState } from "react";
import { Cloud, Thermometer } from "lucide-react";
import api from "../lib/api";

/**
 * WeatherOverlay — polls /api/weather every 10 minutes. Renders a small
 * bottom-right pill (offset above the brand watermark so they don't collide).
 */
export function WeatherOverlay({ location = "", visible }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!visible) return;
    let cancel = false;
    const fetchWeather = () => {
      api.get(`/weather?location=${encodeURIComponent(location || "")}`).then((r) => {
        if (!cancel) setData(r.data);
      }).catch(() => {});
    };
    fetchWeather();
    const id = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => { cancel = true; clearInterval(id); };
  }, [location, visible]);

  if (!visible || !data) return null;
  return (
    <div
      data-testid="weather-overlay"
      className="absolute bottom-20 right-3 z-20 bg-black/70 backdrop-blur rounded-lg px-3 py-2 flex items-center gap-2 shadow-lg border border-white/10"
    >
      <Cloud className="w-4 h-4 text-tv-brand" />
      <div className="flex flex-col leading-tight">
        <div className="text-white text-[11px] font-bold tracking-wide uppercase">{data.location}</div>
        <div className="text-white/70 text-[10px] flex items-center gap-1">
          <Thermometer className="w-2.5 h-2.5" /> {data.temp_f}°F · {data.condition}
        </div>
      </div>
    </div>
  );
}

/**
 * BrbOverlay — "Be Right Back" full-screen takeover. Rendered on top of the
 * video when the director flips it on. Shows the brand logo + editable text,
 * OR a custom uploaded image if provided.
 */
export function BrbOverlay({ visible, text = "Be Right Back", kidsBrand = false, customImageUrl = "" }) {
  if (!visible) return null;
  return (
    <div
      data-testid="brb-overlay"
      className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-5 text-white"
      style={{
        background: customImageUrl
          ? `url(${customImageUrl}) center/cover no-repeat, #0A0A0A`
          : "linear-gradient(135deg, rgba(10,10,10,0.96) 0%, rgba(20,20,20,0.94) 100%)",
      }}
    >
      {!customImageUrl && (
        <img
          src={kidsBrand ? "/tapintv-kids-logo.png" : "/tapintv-logo-v2.png"}
          alt="Tap IN TV"
          className="w-44 sm:w-56 drop-shadow-[0_0_40px_rgba(251,204,24,0.3)]"
        />
      )}
      <div className={`font-display font-black text-4xl sm:text-5xl text-center tracking-tight px-6 ${customImageUrl ? "drop-shadow-[0_4px_20px_rgba(0,0,0,0.9)] bg-black/40 px-6 py-3 rounded-xl" : ""}`}>
        {text}
      </div>
      <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-[0.3em] font-bold">
        <span className="w-2 h-2 rounded-full bg-tv-live animate-pulse" />
        Stream paused · stay tuned
      </div>
    </div>
  );
}

/**
 * UrlPipOverlay — YouTube / Vimeo / generic iframe embed in a draggable
 * PIP slot. Renders only on the host's preview (iframes don't traverse WebRTC).
 */
export function UrlPipOverlay({ url, label = "Embed", visible }) {
  if (!visible || !url) return null;
  const embedUrl = toEmbedUrl(url);
  if (!embedUrl) return null;
  return (
    <div
      data-testid="url-pip-overlay"
      className="absolute top-4 right-4 w-40 md:w-56 aspect-video rounded-lg overflow-hidden border-2 border-tv-brand shadow-2xl bg-black z-20"
    >
      <iframe
        src={embedUrl}
        title="PIP embed"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
      <div className="absolute bottom-1 left-1 right-1 bg-black/70 text-white text-[10px] uppercase tracking-wider font-bold text-center rounded px-1 py-0.5">{label}</div>
    </div>
  );
}

/**
 * Convert any supported social / video URL to an embeddable iframe URL.
 * Returns null if the URL isn't one we know how to embed (so we don't render
 * a broken iframe).
 */
export function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");

    // YouTube (watch?v= and short youtu.be/<id>)
    if (host === "youtube.com" || host === "youtube-nocookie.com" || host === "m.youtube.com") {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}?autoplay=1&mute=1&controls=1`;
      if (u.pathname.startsWith("/embed/")) return url;
      if (u.pathname.startsWith("/shorts/")) return `https://www.youtube.com/embed/${u.pathname.split("/")[2]}?autoplay=1&mute=1`;
    }
    if (host === "youtu.be") {
      const id = u.pathname.slice(1).split("?")[0];
      if (id) return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=1`;
    }

    // Vimeo
    if (host === "vimeo.com") {
      const id = u.pathname.split("/").filter(Boolean)[0];
      if (/^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1`;
    }

    // Facebook video embed
    if (host.endsWith("facebook.com") || host.endsWith("fb.watch")) {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&autoplay=1&mute=1`;
    }

    // TikTok
    if (host.endsWith("tiktok.com")) {
      const match = u.pathname.match(/\/video\/(\d+)/);
      if (match) return `https://www.tiktok.com/embed/v2/${match[1]}`;
    }

    // Instagram reel or post
    if (host.endsWith("instagram.com")) {
      const clean = url.split("?")[0].replace(/\/$/, "");
      return `${clean}/embed`;
    }

    // Twitter / X — returns oEmbed-wrapped timeline; good enough as PIP
    if (host === "twitter.com" || host === "x.com") {
      return `https://platform.twitter.com/embed/Tweet.html?id=${u.pathname.split("/").filter(Boolean).pop()}`;
    }

    // Direct video files — raw URL in a <video> wouldn't iframe; skip
  } catch {
    return null;
  }
  return null;
}
